import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2 } from "lucide-react";

/**
 * ReasonDialog — used for regulated deletes / role changes.
 * The `onConfirm(reason)` callback receives the trimmed reason and is expected to
 * send it via the X-Change-Reason header.
 */
export default function ReasonDialog({
  open, onOpenChange, onConfirm,
  title = "Confirm action",
  description = "This is a regulated action. Please provide a short reason.",
  destructive = true,
  confirmLabel = "Confirm",
  minLength = 5,
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => {
    if (busy) return;
    setReason("");
    onOpenChange(false);
  };

  const submit = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < minLength) return;
    setBusy(true);
    try {
      await onConfirm(trimmed);
      setReason("");
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid="reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {destructive && <AlertTriangle size={16} className="text-red-500" />} {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Change reason <span className="text-muted-foreground">(min {minLength} chars, stored in audit log)</span></Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Duplicate record consolidated with C-042"
            data-testid="reason-input"
          />
          <div className="text-[10px] text-muted-foreground font-mono text-right">{reason.trim().length}/500</div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            onClick={submit}
            disabled={reason.trim().length < minLength || reason.length > 500 || busy}
            data-testid="reason-confirm"
          >
            {busy && <Loader2 size={13} className="animate-spin mr-1.5" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
